/**
 * Notification bell + dropdown rendered against Courier's hosted inbox.
 *
 * Reads `inbox.feeds.inbox.messages` and `totalUnreadCount` from the
 * `useCourier` singleton (signed in by `CourierInboxBridge`). Click on
 * a message marks it read via `inbox.readMessage(...)` and navigates
 * to `data.url`. "Mark all read" calls `inbox.readAllMessages()`.
 */
"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useParams, useRouter } from "next/navigation";
import { toast } from "react-toastify";
import { useCourier } from "@trycourier/courier-react";
import type { InboxMessage } from "@trycourier/courier-react";

import { CommentAuthorAvatar } from "@/components/molecules/comments/CommentAuthorAvatar";
import { useShareCommentStore } from "@/state/shareCommentStore";
import { useSidebarPanelStore } from "@/state/sidebarPanelStore";
import { useTaskLightboxStore } from "@/state/taskLightboxStore";
import { resolveOrbitNotificationAction } from "@/lib/notifications/orbitNotification";
import { trackOrbitNotificationClicked } from "@/lib/analytics/client";
import { api } from "@/lib/trpc/react";
import { LEV_USER_ID } from "@/lib/levai/constants";

const FEED_ID = "inbox";

/**
 * Synthetic author for Lev-sent notifications (people-import lifecycle). The
 * `id` matches the CommentAuthorAvatar Lev branch so the chat logo renders.
 */
const LEV_AUTHOR = {
  id: LEV_USER_ID,
  name: "Lev",
  imageUrl: null,
} as const;

export function NotificationBell({
  className,
  onOpenSettings,
}: {
  className?: string;
  /** Called when the dropdown's Settings link is clicked. The caller is responsible for opening the modal. */
  onOpenSettings: () => void;
}) {
  const router = useRouter();
  // Which project the user is currently viewing (null on the home page /
  // non-project routes). Drives the people-import notification routing below.
  const routeParams = useParams();
  const currentProjectId =
    typeof routeParams?.projectId === "string" ? routeParams.projectId : null;
  const setCommentPlacementMode = useShareCommentStore(
    (s) => s.setCommentPlacementMode,
  );
  const setActivePanel = useSidebarPanelStore((s) => s.setActivePanel);
  // Open a task + scroll to a comment directly when the recipient is already on
  // the project page (a Next router.push to a same-page `#task=` hash doesn't
  // re-fire the deep-link hook's hashchange listener).
  const openTaskLightbox = useTaskLightboxStore((s) => s.openTask);
  const { inbox } = useCourier();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const messages: InboxMessage[] = useMemo(
    () => inbox.feeds[FEED_ID]?.messages ?? [],
    [inbox.feeds],
  );
  const unreadCount = inbox.totalUnreadCount ?? 0;

  // ---------------------------------------------------------------------------
  // Notification metadata (author + resolved flag) for the messages currently
  // shown in the bell. Drives sender avatars (#10) and the pre-navigation
  // resolved-check (#2). Looked up in a single batch query keyed on the
  // commentIds we extracted from the tags.
  // ---------------------------------------------------------------------------
  const commentIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of messages) {
      const id = idsFromTags(m.tags)?.commentId;
      if (id && /^c[a-z0-9]{24}$/.test(id)) ids.add(id);
    }
    // `comment.notificationMeta` accepts max 50 commentIds per call. Cap
    // at 50 so a large historical inbox doesn't throw the entire bell
    // query; the most recent items are kept, which is what visibly
    // matters for the resolved-check and avatar lookups (older rows scroll
    // off the visible feed anyway).
    return Array.from(ids).slice(0, 50);
  }, [messages]);

  const { data: metaData } = api.comment.notificationMeta.useQuery(
    { commentIds },
    {
      enabled: commentIds.length > 0,
      staleTime: 60_000,
    },
  );

  const metaByCommentId = useMemo(() => {
    const map = new Map<
      string,
      {
        hidden: boolean;
        author: { id: string; name: string | null; imageUrl: string | null } | null;
      }
    >();
    for (const item of metaData?.items ?? []) {
      map.set(item.id, { hidden: item.hidden, author: item.author });
    }
    return map;
  }, [metaData]);

  // Extracted as a `useCallback` so the same logic powers both the dropdown
  // row's onClick and the new-arrival toast's onClick (LEV-659 round 2 #16):
  // clicking either lands the user on the canonical
  // `/{projectId}?commentId=…` URL, with the same resolved-comment guard.
  const handleItemClick = useCallback(
    (m: InboxMessage) => {
      if (!m.read) {
        void inbox.readMessage(m);
      }
      setOpen(false);

      // Build the deep-link client-side from message tags. Courier strips
      // arbitrary message data and doesn't interpolate `{var}` inside
      // elemental action.data, so the only reliable place we can stash the
      // identifiers we need is `metadata.tags` (preserved verbatim).
      const ids = idsFromTags(m.tags);

      // LEV-1070 (reconciled with LEV-1044): "Lev added N people for your Orbit"
      // (and the import-started sibling) open Orbit rather than a comment
      // thread, routed by where the user currently is — never a hard refresh.
      // Inside a project → open THAT project's Orbit lightbox inline (don't
      // navigate away). On the home page → soft-navigate to the Orbit People
      // import-history page (LEV-1044's surface). The static Courier url is
      // only the email/out-of-app fallback; we deliberately don't follow it
      // for in-app clicks.
      if (isPeopleImportMessage(m.tags)) {
        const action = resolveOrbitNotificationAction({ currentProjectId });
        if (action.mode === "inline") {
          trackOrbitNotificationClicked({
            mode: "inline",
            projectId: action.projectId,
          });
          setActivePanel("people");
        } else {
          trackOrbitNotificationClicked({ mode: "navigate" });
          router.push(action.path);
        }
        return;
      }

      // Roadmap-task @-mention: route by the ActivityEntry id (`rc:`) since the
      // roadmap node id can exceed Courier's tag limit. The `?roadmapComment=`
      // consumer resolves the node from the entry and opens the roadmap lightbox.
      // A same-path query-param push is a client-side navigation (no reload) that
      // re-triggers the consumer's `useSearchParams` effect — unlike a hash push.
      if (ids?.projectId && ids.roadmapCommentId) {
        router.push(
          `/${ids.projectId}?roadmapComment=${ids.roadmapCommentId}`,
        );
        return;
      }

      // Task @-mention (task-sharing Phase 2): "<author> tagged you in a
      // comment". Open the TASK lightbox and scroll to the mentioned comment.
      // Distinguished from a canvas comment by the `t:` (taskId) tag.
      if (ids?.projectId && ids.taskId && ids.commentId) {
        if (currentProjectId === ids.projectId) {
          // Already on the project — open directly (a same-page hash push won't
          // re-trigger the deep-link hook). Focus the mentioned comment.
          openTaskLightbox(ids.taskId, "notification", ids.commentId);
        } else {
          // Cross-page: the deep-link hook reads `taskComment` (which entry to
          // focus) and `taskSurface` (so the open still counts as `notification`
          // instead of the hook's generic `deep_link` default) off the URL.
          router.push(taskNotificationHref(ids.projectId, ids.taskId, ids.commentId));
        }
        return;
      }

      // Task-report notification (LEV-1046): deep-link straight to the task
      // lightbox via the `#task=` hash — never comment-placement mode.
      if (ids?.projectId && ids.taskId && !ids.commentId) {
        if (currentProjectId === ids.projectId) {
          // Already on the project — open directly, same reasoning as the
          // mention branch above (no reload to carry a surface across).
          openTaskLightbox(ids.taskId, "notification");
        } else {
          router.push(taskNotificationHref(ids.projectId, ids.taskId));
        }
        return;
      }

      if (ids?.projectId) {
        // #2: if the comment thread has already been resolved (hidden),
        // skip the commentId/blockId in the URL so the lightbox doesn't
        // try to open a thread that no longer renders, and tell the user
        // explicitly. We still land them on the unified project URL.
        const meta = ids.commentId ? metaByCommentId.get(ids.commentId) : null;
        if (ids.commentId && meta?.hidden) {
          toast.info("This comment was already resolved.", { autoClose: 4000 });
          router.push(`/${ids.projectId}`);
          return;
        }

        const params = new URLSearchParams();
        if (ids.blockId) params.set("blockId", ids.blockId);
        if (ids.commentId) params.set("commentId", ids.commentId);
        // LEV-659 #11: navigate to the unified `/{projectId}` entry; the
        // server route forks to the role-appropriate canvas page.
        const path = `/${ids.projectId}${
          params.size ? `?${params.toString()}` : ""
        }`;
        // Comment mode on so pins are visible behind the lightbox.
        if (ids.commentId) setCommentPlacementMode(true);
        router.push(path);
        return;
      }

      // Fallback: follow Courier's tracked redirect (full reload).
      const fallbackHref = m.actions?.[0]?.href ?? null;
      if (fallbackHref) window.location.href = fallbackHref;
    },
    [
      inbox,
      metaByCommentId,
      currentProjectId,
      router,
      setActivePanel,
      setCommentPlacementMode,
      openTaskLightbox,
    ],
  );

  // ---------------------------------------------------------------------------
  // Toast on new arrivals (#3). We compare incoming messageIds against the set
  // we've already seen; on subsequent renders, any brand-new id triggers a
  // single toast. The first render seeds the set without toasting (otherwise
  // every page reload would dump the entire inbox into toasts).
  // ---------------------------------------------------------------------------
  const seenMessageIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    // Wait for Courier's first non-empty load before seeding. The bell
    // mounts before the inbox finishes hydrating, so an early seed
    // against an empty array would mark every subsequently-loaded
    // historical-unread row as a "new arrival" on the next render and
    // toast each one. (LEV-659 #3 follow-up.)
    if (messages.length === 0 && seenMessageIdsRef.current === null) return;
    const current = new Set(messages.map((m) => m.messageId));
    if (seenMessageIdsRef.current === null) {
      // First observation with real messages — seed quietly.
      seenMessageIdsRef.current = current;
      return;
    }
    const prev = seenMessageIdsRef.current;
    for (const m of messages) {
      if (prev.has(m.messageId)) continue;
      // Only toast genuinely-new arrivals (unread + recent). Courier paginates
      // older messages in over time on `inbox.load()`, which would otherwise
      // look like "new" to this diff.
      if (m.read) continue;
      // LEV-1018 R2: the CSV/people-import flow owns its own foreground toasts
      // (an info "Adding people" on start + a success "Added N" on completion,
      // fired by CsvImportModal). Suppressing the bell's arrival toast for
      // `people-import:*` messages prevents the duplicate blue toasts the
      // reviewer saw. These notifications still land in the bell inbox below —
      // we only skip the transient toast, not the record.
      if (isPeopleImportMessage(m.tags)) continue;
      const title = m.title?.trim() ?? "";
      const preview = (m.preview ?? m.body ?? "").trim();
      // Title is "<author> commented in <project>". Pull out author + project
      // so the toast can render the two lines we actually want (#15):
      //   Line 1 (primary):  "<author> commented <preview…>"
      //   Line 2 (muted):    "<project name>"
      // Toast click routes through the same `handleItemClick` as the
      // dropdown row (LEV-659 round 2 #16) — same resolved-comment guard,
      // same `/{projectId}?commentId=…` navigation.
      const parsed = parseCommentedInTitle(title);
      const previewSnippet = truncatePreview(preview, 40);
      // Task @-mentions get the explicit "<author> tagged you in a comment"
      // headline; the comment text drops to the muted second line. Roadmap-task
      // mentions share the same copy (only their deep-link routing differs).
      const isTaskMention =
        isTaskMentionMessage(m.tags) || isRoadmapMentionMessage(m.tags);
      const authorName = isTaskMention
        ? parseNotificationAuthor(title) ?? "Someone"
        : parsed?.author ?? "";
      const projectName = isTaskMention ? previewSnippet : parsed?.project ?? "";
      const primary = isTaskMention
        ? `${authorName} tagged you in a comment`
        : authorName && previewSnippet
          ? `${authorName} commented ${previewSnippet}`
          : authorName
            ? `${authorName} commented`
            : previewSnippet || title || "New notification";
      toast.info(
        <div>
          <div className="text-sm font-medium text-slate-900">{primary}</div>
          {projectName ? (
            <div className="mt-0.5 text-xs text-slate-500">{projectName}</div>
          ) : null}
        </div>,
        {
          autoClose: 5000,
          onClick: () => handleItemClick(m),
        },
      );
    }
    seenMessageIdsRef.current = current;
  }, [messages, handleItemClick]);

  // Click-outside-to-close.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={
          className ??
          "group relative p-3 rounded-lg text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
        }
        aria-label={
          unreadCount > 0
            ? `Notifications — ${unreadCount} unread`
            : "Notifications"
        }
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-medium text-white ring-2 ring-white"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={dropdownRef}
          role="menu"
          aria-label="Notifications"
          className="absolute left-full bottom-0 z-modal ml-2 flex w-96 flex-col rounded-lg border border-slate-200 bg-white shadow-xl"
        >
          <div className="flex items-center justify-between border-b border-slate-100 p-3">
            <span className="text-sm font-semibold text-slate-900">Notifications</span>
            <div className="flex items-center gap-2 text-xs">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => void inbox.readAllMessages()}
                  className="text-slate-500 hover:text-slate-800"
                >
                  Mark all read
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onOpenSettings();
                }}
                className="text-slate-500 hover:text-slate-800"
              >
                Settings
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {messages.length === 0 && (
              <p className="p-4 text-xs text-slate-500">
                No notifications yet. We&rsquo;ll let you know when there&rsquo;s activity.
              </p>
            )}
            {messages.map((m) => {
              // Courier strips top-level `data` from inbox messages, so the
              // headline is the template's `title` ("<author> commented in
              // <project>"). The kind ("Mentioned you" / "New reply" /
              // "New comment") is already implicit in the body preview, so
              // we don't repeat it here.
              const rawTitle = m.title ?? "";
              // Task @-mentions get the sharper, explicit "<author> tagged you
              // in a comment" headline instead of the generic template title.
              const titleText =
                isTaskMentionMessage(m.tags) || isRoadmapMentionMessage(m.tags)
                  ? `${parseNotificationAuthor(rawTitle) ?? "Someone"} tagged you in a comment`
                  : rawTitle;
              const previewText = m.preview ?? m.body ?? "";
              const ids = idsFromTags(m.tags);
              // People-import notifications come from Lev (LEV-1044) and carry
              // no commentId to resolve a Clerk author from, so attribute them
              // to the Lev system user — that hits the CommentAuthorAvatar Lev
              // branch (the chat logo) instead of the seeded-initial fallback.
              const author = isPeopleImportMessage(m.tags)
                ? LEV_AUTHOR
                : ids?.commentId
                  ? metaByCommentId.get(ids.commentId)?.author ?? null
                  : null;
              return (
                <button
                  key={m.messageId}
                  type="button"
                  onClick={() => handleItemClick(m)}
                  className={`flex w-full items-start gap-3 border-b border-slate-50 p-3 text-left text-sm hover:bg-slate-50 ${
                    m.read ? "" : "bg-slate-50/50"
                  }`}
                >
                  <CommentAuthorAvatar
                    authorUser={author}
                    // Legacy Courier messages (pre-2026-05-12) sometimes
                    // carry no `commentId` tag and no `title` — without the
                    // preview fallback those rows render a purple `?`
                    // placeholder instead of a seeded initial. (LEV-659 #10
                    // follow-up.)
                    fallbackLabel={titleText || previewText || "Notification"}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-xs font-medium text-slate-900">
                        {titleText}
                      </span>
                      {!m.read && (
                        <span
                          aria-hidden
                          className="h-2 w-2 flex-shrink-0 rounded-full bg-rose-500"
                        />
                      )}
                    </div>
                    {previewText && (
                      <p className="line-clamp-2 text-xs text-slate-600">
                        {previewText}
                      </p>
                    )}
                    {m.created && (
                      <p className="mt-0.5 text-[10px] text-slate-400">
                        {new Date(m.created).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Parse a Courier notification title of the form
 * "<author> commented in <project>" into its parts. Returns null when the
 * title doesn't fit that template (e.g. legacy or non-comment notifications).
 */
function parseCommentedInTitle(
  title: string,
): { author: string; project: string } | null {
  if (!title) return null;
  const match = /^(.+?)\s+commented in\s+(.+)$/i.exec(title.trim());
  if (!match) return null;
  const author = match[1]?.trim() ?? "";
  const project = match[2]?.trim() ?? "";
  if (!author || !project) return null;
  return { author, project };
}

/**
 * Whether a Courier inbox message is a task @-mention ("tagged you in a
 * comment") — it carries BOTH a task id (`t:`) and a comment id (`c:`). A canvas
 * comment has `c:` but no `t:`; a task-report has `t:` but no `c:`, so the pair
 * uniquely identifies a task-comment mention.
 */
export function isTaskMentionMessage(tags?: string[]): boolean {
  const ids = idsFromTags(tags);
  return !!(ids?.taskId && ids?.commentId);
}

/**
 * Whether a message is a roadmap-task @-mention — it carries the `rc:`
 * (roadmapComment) tag. Renders with the same "tagged you in a comment" headline
 * as {@link isTaskMentionMessage}; only the deep-link routing differs.
 */
export function isRoadmapMentionMessage(tags?: string[]): boolean {
  return !!idsFromTags(tags)?.roadmapCommentId;
}

/**
 * Extract the author's display name from a Courier comment/mention title of the
 * form "<author> mentioned you in <project>" / "<author> commented in <project>".
 * Drives the sharper "<author> tagged you in a comment" headline. Returns null
 * when the title doesn't start with a recognizable author clause.
 */
export function parseNotificationAuthor(title: string): string | null {
  if (!title) return null;
  const match = /^(.+?)\s+(?:mentioned you|tagged you|commented|replied)/i.exec(
    title.trim(),
  );
  return match?.[1]?.trim() || null;
}

/**
 * Trim a preview string to at most `max` characters, appending an ellipsis
 * when it was shortened. Whitespace is collapsed so multi-line comment
 * previews don't blow up the toast line height.
 */
function truncatePreview(preview: string, max: number): string {
  const collapsed = preview.replace(/\s+/g, " ").trim();
  if (collapsed.length <= max) return collapsed;
  return `${collapsed.slice(0, max).trimEnd()}…`;
}

/**
 * Whether a Courier inbox message is a people-import lifecycle notification
 * (start/finish). `sendPeopleImportNotification` stamps a `people-import:<phase>`
 * tag on every send (see `src/lib/courier/client.ts`). Terminal sends carry the
 * real phase (`completed`/`failed`/`canceled`) since LEV-1141, so this must
 * match on the tag PREFIX rather than an exact phase list. The
 * import modal owns the foreground toasts for these, so the bell skips its
 * arrival toast for them while still listing them in the dropdown (LEV-1018 R2).
 */
export function isPeopleImportMessage(tags?: string[]): boolean {
  if (!tags) return false;
  return tags.some((t) => t.startsWith("people-import:"));
}

/**
 * The cross-project URL for a task @-mention or task-report notification
 * click (`useTaskLightboxDeepLink` reads both params off the URL once the
 * canvas mounts). `taskSurface=notification` is the only way a notification
 * can carry its surface across a full navigation — a same-project click skips
 * this entirely and calls `openTaskLightbox` directly instead. `commentId` is
 * omitted for a task-report notification, which has no comment to focus.
 */
export function taskNotificationHref(
  projectId: string,
  taskId: string,
  commentId?: string | null,
): string {
  const params = new URLSearchParams();
  if (commentId) params.set("taskComment", commentId);
  params.set("taskSurface", "notification");
  return `/${projectId}?${params.toString()}#task=${taskId}`;
}

/**
 * Pull the deep-link identifiers (projectId, blockId, commentId) out of
 * the tags the courier client stamps on every comment send. Each is a
 * cuid prefixed with `p:` / `b:` / `c:`.
 */
// Parses the deep-link tags the server stashes on the Courier message:
// `p:` projectId, `b:` blockId, `c:` commentId, `t:` taskId (task-report
// notifications route via `t:` to the task lightbox), `rc:` roadmapCommentId
// (a roadmap-task @-mention — keyed by the ActivityEntry id, since roadmap node
// ids can exceed Courier's 30-char tag limit; the node is resolved from the entry
// by the `?roadmapComment=` deep-link consumer).
function idsFromTags(tags?: string[]): {
  projectId?: string;
  blockId?: string;
  commentId?: string;
  taskId?: string;
  roadmapCommentId?: string;
} | null {
  if (!tags) return null;
  const out: {
    projectId?: string;
    blockId?: string;
    commentId?: string;
    taskId?: string;
    roadmapCommentId?: string;
  } = {};
  for (const t of tags) {
    // `rc:` before `c:` — both start with "c" only after the prefix, but an
    // ordered check keeps intent explicit (rc is its own two-char prefix).
    if (t.startsWith("rc:")) out.roadmapCommentId = t.slice(3);
    else if (t.startsWith("p:")) out.projectId = t.slice(2);
    else if (t.startsWith("b:")) out.blockId = t.slice(2);
    else if (t.startsWith("c:")) out.commentId = t.slice(2);
    else if (t.startsWith("t:")) out.taskId = t.slice(2); // task-report deep-link
  }
  return out;
}
